import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Plus, Printer, CheckCircle2 } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import UploadBereich from '../components/UploadBereich';
import { BASIS, type Auftrag, type AuftragKurz } from '../lib/typen';
import { verkleinereBild, fehlerText } from '../lib/dateien';

export default function UebersichtPage({ base }: { base: string }) {
  const navigate = useNavigate();
  const [auftraege, setAuftraege] = useState<AuftragKurz[] | null>(null);
  const [liest, setLiest] = useState(false);

  useEffect(() => {
    api.get(`${BASIS}/auftraege`)
      .then((r) => setAuftraege(r.data.data))
      .catch(async (e) => { toast.error(await fehlerText(e, 'Aufträge konnten nicht geladen werden')); setAuftraege([]); });
  }, []);

  async function erkennen(dateien: File[]) {
    setLiest(true);
    try {
      const form = new FormData();
      for (const d of dateien) form.append('dateien', await verkleinereBild(d));
      const r = await api.post(`${BASIS}/erkennen`, form, { timeout: 240_000 });
      const d = r.data.data;
      const entwurf: Auftrag = {
        titel: d.titel || dateien[0].name.replace(/\.\w+$/, ''),
        artikel: d.artikel,
        hinweise: d.hinweise,
        quelleDateiname: d.quelleDateiname,
        positionen: d.positionen,
        kontrollsummen: d.kontrollsummen,
      };
      const unsicher = d.positionen.filter((p: any) => p.unsicher).length;
      toast.success(`${d.positionen.length} Positionen erkannt${unsicher ? ` — ${unsicher} bitte prüfen` : ''}`);
      navigate(`${base}/neu`, { state: { entwurf } });
    } catch (e) {
      toast.error(await fehlerText(e, 'Erkennung fehlgeschlagen'));
    } finally {
      setLiest(false);
    }
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Druck-Initialen</h1>
          <p className="text-sm text-gray-500">Kundenliste einlesen → prüfen → Druckbogen (SVG) für CorelDRAW</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`${base}/neu`)}>
          <Plus className="mr-1 h-4 w-4" /> Leere Liste
        </Button>
      </div>

      <UploadBereich laeuft={liest} onDateien={erkennen} />

      <div className="rounded-lg border border-[#DAD9D9] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#1A1A1A] text-left text-white">
            <tr>
              <th className="px-3 py-2">Auftrag</th>
              <th className="px-3 py-2">Artikel</th>
              <th className="px-3 py-2 text-right">Stück</th>
              <th className="px-3 py-2">Größen</th>
              <th className="px-3 py-2">Erledigt</th>
              <th className="px-3 py-2">Angelegt</th>
            </tr>
          </thead>
          <tbody>
            {auftraege === null && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">Lädt …</td></tr>
            )}
            {auftraege?.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">Noch keine Aufträge gespeichert.</td></tr>
            )}
            {auftraege?.map((a) => (
              <tr key={a.id} onClick={() => navigate(`${base}/auftrag/${a.id}`)} className="cursor-pointer border-t border-[#DAD9D9] hover:bg-[#E9FF57]/20">
                <td className="px-3 py-2 font-medium">
                  {a.titel}
                  {a.gedrucktAm && <Printer className="ml-2 inline h-4 w-4 text-green-600" aria-label="gedruckt" />}
                </td>
                <td className="px-3 py-2 text-gray-600">{a.artikel || '–'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{a.anzahl}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {a.groessen.map((g) => (
                      <span key={g.groesse} className="rounded bg-[#DAD9D9]/60 px-1.5 text-xs">
                        {g.groesse === 'OHNE' ? '–' : g.groesse}: {g.anzahl}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {a.erledigt === a.positionen && a.positionen > 0 ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <span className="text-gray-500">{a.erledigt}/{a.positionen}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-500">{new Date(a.createdAt).toLocaleDateString('de-DE')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
